<template>
    <div class="bcmp" :style="{ '--color1': sides[0].color, '--color2': sides[1].color }">
        <!-- who is compared: team logos, or player photos over their team's logo -->
        <div class="bcmp__sides">
            <div v-for="(side, index) in sides" :key="index" class="bcmp-side" :class="{ 'bcmp-side--right': index === 1, 'bcmp-side--player': side.photo }">
                <span v-if="side.photo" class="bcmp-side__photo" :style="{ borderColor: side.color }">
                    <img v-if="side.logo" :src="side.logo" alt="" class="bcmp-side__watermark">
                    <img :src="side.photo" alt="" class="bcmp-side__img" @error="$event.target.src = defaultPlayerImg">
                </span>
                <span v-else class="bcmp-side__logo">
                    <img v-if="side.logo" :src="side.logo" alt="">
                </span>
                <span class="bcmp-side__text">
                    <span class="bcmp-side__name">{{ side.name }}</span>
                    <span v-if="side.sub" class="bcmp-side__sub">{{ side.sub }}</span>
                </span>
            </div>
        </div>

        <div v-for="row in computedRows" :key="row.key" class="bcmp-row">
            <div class="bcmp-value" :class="{ 'bcmp-value--lead': row.lead === 1 }">
                {{ row.display1 }}
                <small v-if="row.detail1">{{ row.detail1 }}</small>
            </div>
            <div class="bcmp-middle">
                <span class="bcmp-label">{{ row.label }}</span>
                <div class="bcmp-bars">
                    <div class="bcmp-bar bcmp-bar--left"><span :style="{ width: `${row.share1}%` }" /></div>
                    <div class="bcmp-bar bcmp-bar--right"><span :style="{ width: `${row.share2}%` }" /></div>
                </div>
            </div>
            <div class="bcmp-value bcmp-value--right" :class="{ 'bcmp-value--lead': row.lead === 2 }">
                {{ row.display2 }}
                <small v-if="row.detail2">{{ row.detail2 }}</small>
            </div>
        </div>
    </div>
</template>

<script>
    // Two sides compared row by row for the live stream: the better value bigger and darker,
    // bars growing from the middle in the colour of each side
    export default {
        name: 'BroadcastCompare',
        props: {
            // [{ name, sub, logo, photo, color }] x 2
            sides: {
                type: Array,
                required: true
            },
            // [{ key, label, value1, value2, display1, display2, detail1, detail2, lowerIsBetter }]
            rows: {
                type: Array,
                default: () => []
            }
        },
        data() {
            return {
                defaultPlayerImg: require('@/assets/default_player.png')
            }
        },
        computed: {
            computedRows() {
                return this.rows.map((row) => {
                    const value1 = Number(row.value1) || 0
                    const value2 = Number(row.value2) || 0
                    const total = Math.abs(value1) + Math.abs(value2)
                    let lead = 0
                    if (value1 !== value2) lead = (value1 > value2) !== !!row.lowerIsBetter ? 1 : 2
                    return {
                        ...row,
                        display1: row.display1 ?? value1,
                        display2: row.display2 ?? value2,
                        lead,
                        share1: total ? Math.round(Math.abs(value1) / total * 100) : 0,
                        share2: total ? Math.round(Math.abs(value2) / total * 100) : 0
                    }
                })
            }
        }
    }
</script>

<style scoped>
.bcmp {
    font-family: Ubuntu, Arial, sans-serif;
}

.bcmp__sides {
    display: flex;
    justify-content: space-between;
    gap: 24px;
    padding-bottom: 14px;
    margin-bottom: 4px;
    border-bottom: 2px solid rgba(0, 0, 0, 0.08);
}

.bcmp-side {
    display: flex;
    align-items: center;
    gap: 16px;
    min-width: 0;
}

.bcmp-side--right {
    flex-direction: row-reverse;
    text-align: right;
}

.bcmp-side__logo {
    display: inline-flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;
    width: 72px;
    height: 72px;
    padding: 5px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 3px 12px rgba(0, 0, 0, 0.16);
}

.bcmp-side__logo img {
    width: 100%;
    height: 100%;
    object-fit: contain;
}

.bcmp-side__photo {
    position: relative;
    flex: 0 0 auto;
    width: 120px;
    height: 120px;
    border: 4px solid;
    border-radius: 50%;
    background: #f4f4f6;
    overflow: hidden;
}

.bcmp-side__watermark {
    position: absolute;
    inset: -10%;
    width: 120%;
    height: 120%;
    object-fit: contain;
    opacity: 0.18;
}

.bcmp-side__img {
    position: relative;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top;
}

.bcmp-side__text {
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.bcmp-side__name {
    font-family: Mansalva, Arial, sans-serif;
    font-size: 2rem;
    font-weight: 800;
    line-height: 1.15;
}

.bcmp-side__sub {
    color: rgba(0, 0, 0, 0.5);
    font-size: 1rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
}

/* ---------- Rows ---------- */
.bcmp-row {
    display: grid;
    grid-template-columns: 170px 1fr 170px;
    align-items: center;
    gap: 20px;
    padding: 7px 0;
    border-bottom: 1px solid rgba(0, 0, 0, 0.05);
}

.bcmp-row:last-child {
    border-bottom: 0;
}

.bcmp-value {
    display: flex;
    flex-direction: column;
    color: rgba(0, 0, 0, 0.55);
    font-size: 1.7rem;
    font-weight: 700;
    line-height: 1.1;
    font-variant-numeric: tabular-nums;
}

.bcmp-value--right {
    align-items: flex-end;
    text-align: right;
}

.bcmp-value small {
    color: rgba(0, 0, 0, 0.45);
    font-size: 0.95rem;
    font-weight: 600;
}

.bcmp-value--lead {
    color: #111;
    font-size: 2rem;
    font-weight: 800;
}

.bcmp-middle {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
}

.bcmp-label {
    color: rgba(0, 0, 0, 0.7);
    font-size: 1.25rem;
    font-weight: 700;
}

.bcmp-bars {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6px;
    width: 100%;
}

.bcmp-bar {
    display: flex;
    height: 12px;
    border-radius: 6px;
    background: #f1f1f3;
    overflow: hidden;
}

.bcmp-bar--left {
    justify-content: flex-end;
}

.bcmp-bar span {
    display: block;
    height: 100%;
    border-radius: 6px;
}

.bcmp-bar--left span {
    background: var(--color1);
}

.bcmp-bar--right span {
    background: var(--color2);
}
</style>
